import React from 'react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgWindowMinimize from '@icons/window-minimize.svg?react';
import SvgWindowFloat from '@icons/window-float.svg?react';
import SvgDockLeft from '@icons/arrow-left-from-line.svg?react';
import SvgDockRight from '@icons/arrow-right-to-line.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { PanelDockSide } from './panelDockStore.ts';
import { PanelDock } from './usePanelDock.ts';

interface ComponentProps {
  dock: PanelDock;
}

// Sized like CloseButton — a 12px glyph in a 32px hit area, negative margins so the row keeps its height.
const BUTTON_CLASS = '-my-2 h-auto p-2';
const ICON_CLASS = 'h-3 w-3';

/**
 * The dock actions for a panel header, placed just before its CloseButton.
 *
 * - Docked: move to the other edge (one button, pointing where it would go), float, minimize.
 * - Floating: dock left, dock right, minimize.
 *
 * Moving and floating are hidden below the lg breakpoint, where panels are full width and there is no
 * other edge to go to. Renders nothing while the profile switch is off.
 */
const PanelDockControls: React.FC<ComponentProps> = ({ dock }) => {
  const { tr } = useTranslationLgs();
  if (!dock.enabled) return null;

  const dockButton = (side: PanelDockSide) => (
    <RingilButton
      key={side}
      mode={'link'}
      onClick={() => dock.dock(side)}
      icon={side === 'left' ? <SvgDockLeft className={ICON_CLASS} /> : <SvgDockRight className={ICON_CLASS} />}
      tooltip={side === 'left' ? tr('PanelDock.dockLeft', 'Přesunout doleva') : tr('PanelDock.dockRight', 'Přesunout doprava')}
      className={`${BUTTON_CLASS} max-lg:hidden`}
      dataTestId={`panelDock-${dock.key}-dock-${side}`}
    />
  );

  return (
    <>
      {dock.mode === 'float' ? (
        <>
          {dockButton('left')}
          {dockButton('right')}
        </>
      ) : (
        <>
          {dockButton(dock.side === 'right' ? 'left' : 'right')}
          {dock.canFloat && (
            <RingilButton
              mode={'link'}
              onClick={dock.float}
              icon={<SvgWindowFloat className={ICON_CLASS} />}
              tooltip={tr('PanelDock.float', 'Uvolnit jako okno — nebo přetáhněte záhlaví')}
              className={BUTTON_CLASS}
              dataTestId={`panelDock-${dock.key}-float`}
            />
          )}
        </>
      )}
      <RingilButton
        mode={'link'}
        onClick={dock.minimize}
        icon={<SvgWindowMinimize className={ICON_CLASS} />}
        tooltip={tr('PanelDock.minimize', 'Minimalizovat')}
        className={BUTTON_CLASS}
        dataTestId={`panelDock-${dock.key}-minimize`}
      />
    </>
  );
};

export default PanelDockControls;
